import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CTABanner } from "../components/CTABanner";
import { PROJECT_CATEGORIES } from "../data/site";
import { useSite } from "../context/ContentContext";

export default function Projects() {
  const { projects: PROJECTS } = useSite();
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <Seo title="Projects" description="Explore completed projects by Domaleo Enterprise across trade, IT, agriculture, logistics and general supply." />

      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-px mx-auto max-w-7xl">
          <Reveal>
            <p className="overline text-accent">Our Work</p>
            <h1 className="mt-3 max-w-3xl text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance">Projects that delivered real outcomes</h1>
            <p className="mt-5 max-w-2xl text-lg text-slate-200 leading-relaxed">A selection of completed engagements across our five service lines.</p>
          </Reveal>
        </div>
      </section>

      <section className="container-px mx-auto max-w-7xl py-16 lg:py-24">
        <div className="flex flex-wrap gap-3" data-testid="project-filters">
          {PROJECT_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`project-filter-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-all duration-200 ${
                filter === c ? "bg-primary text-primary-foreground shadow-md" : "border border-border bg-card text-foreground/80 hover:border-primary hover:text-primary"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.article
                key={p.title}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.35 }}
                className="group overflow-hidden rounded-2xl border border-border bg-card"
                data-testid="project-card"
              >
                <div className="relative h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-primary backdrop-blur">{p.category}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-lg font-semibold flex items-start justify-between gap-2">
                    {p.title}
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.description}</p>
                  <p className="mt-4 inline-flex rounded-lg bg-secondary/10 px-3 py-1.5 text-sm font-medium text-secondary">{p.outcome}</p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <CTABanner />
    </div>
  );
}
